import React from 'react';
import { Subtract } from 'utility-types';

import { Filter } from '~commonInfra/utils/Filter';
import { RequestAccessError } from '~src/requestAccess/components/RequestAccessError';
import { SpacesSiteUrls } from '~src/spacesPlatform/components/spaceComponents/SpacesSiteUrls';

import { RouterError } from '../../bootstrap/routerHelpers/RouterError';
import { Projects } from '../../projects/resources/Projects';
import { INamedItemInfo } from '../../spacesPlatform/models/resourceInfoParts';
import { ISimulationDetailInfo, ISimulationInfo } from '../models/ISimulationInfo';
import { Simulations } from '../resources/Simulations';
import { ISimulationActions } from '../serverApi/types/ISimulationActions';

interface InjectedSimulationSpaceData {
  readonly simulationAllowedActions: ISimulationActions;
  readonly simulationDetailInfo: ISimulationDetailInfo;
  readonly simulationInfo: ISimulationInfo;
  readonly projectHierarchy: INamedItemInfo[];
}

export interface InjectedSimulationSpaceDataFetcherProps extends InjectedSimulationSpaceData {
  readonly refreshData: () => void;
}

export interface SimulationSpaceDataFetcherProps {
  readonly siteName: string;
  readonly simulationId: string;
  readonly siteUrls: SpacesSiteUrls;
  readonly isRequestAccessEnabled: boolean;
}

export function withSimulationSpaceDataFetcher<P extends InjectedSimulationSpaceDataFetcherProps>(
  Component: React.ComponentType<P>
) {
  return function SimulationSpaceDataFetcher(
    props: Subtract<P, InjectedSimulationSpaceDataFetcherProps> & SimulationSpaceDataFetcherProps
  ) {
    const [ error, setError ] = React.useState(false);
    const [ injectedData, setInjectedData ] = React.useState<InjectedSimulationSpaceData>(undefined);
    const [ errorCode, setErrorCode ] = React.useState(null);

    let didCancel = false;

    const refreshData = () => {
      fetchSimulationSpaceData(props.simulationId)
        .then(injectedData => {
          !didCancel && setError(false);
          !didCancel && setInjectedData(injectedData);
        })
        .catch((e) => {
          !didCancel && setError(true);
          !didCancel && setErrorCode(e);
        });
    };

    React.useEffect(() => {
      refreshData();
      // eslint-disable-next-line react-hooks/exhaustive-deps
      return () => didCancel = true;
    }, [ props.simulationId, props.siteName ]);

    if (error) {
      if (props.isRequestAccessEnabled) {
        return (
          <RequestAccessError
            url={props.siteUrls.exploreUrl()}
            contentIdentifier={props.simulationId}
            contentType={'simulation'}
            errorCode={errorCode}
          />
        );
      }
      return <RouterError errorType={'simulation'} urls={props.siteUrls}/>;
    } else if (!injectedData) {
      return null;
    }

    return <Component {...props as unknown as P} {...injectedData} refreshData={refreshData}/>;
  };
}

async function fetchSimulationSpaceData(simulationId: string): Promise<InjectedSimulationSpaceData> {
  const simulationAllowedActions = await Simulations.actions(simulationId);
  const simulationDetailInfo = await Simulations.getById(simulationId);
  const simulationInfo = await Simulations.all()
    .withFilters([Filter.fieldEq('id', simulationId)])
    .getFirst();

  let projectHierarchy: INamedItemInfo[] = [];
  if (simulationDetailInfo.project) {
    projectHierarchy = await Projects.getProjectHierarchy(simulationDetailInfo.project.id);
  }

  return {
    simulationAllowedActions,
    simulationDetailInfo,
    simulationInfo,
    projectHierarchy,
  };
}
